import merge from "deepmerge";
import { green, grey, indigo, red } from "@mui/material/colors";
import { THEMES } from "../../constants";

const customColor = {
  50: "#e9f0fb",
  100: "#c8daf4",
  200: "#a3c1ed",
  300: "#7ea8e5",
  400: "#6395e0",
  500: "#FF8C26",
  600: "#407ad6",
  700: "#0b3980",
  800: "#2f65cb",
  900: "#2052c2 ",
};

export const Colors = {
  primaryColor: "#13214e",
  primaryColorLight: "#465586",
  primaryColorDark: "#061134",
  primaryColorNeutralBlue: "#7F90C9",
  primaryDarkBlue: "#061134",
  secondaryColor: "#D78A17",
  secondaryColorLight: "#EBA742",
  secoundaryColorNeutral: "#FFD493",
  secoundaryColorDark: "#855000",
  textColorGray: "#565757",
  textColorBlack: "#00030C",
  textColorLight: "#F8F9FC",
  neutralColor: "#9FA5A5",
  neutralColorLight: "#DDDDDD",
  neutralColorSurface: "#F1F0F0",
  neutralColorWhite: "#ffffff",
  neutralCOlorBlue: "#E6EBFA",
  neutralColorWarm: "#D8D8D8",
  feedbackSuccess: "#006B00",
  feedbackError: "#C60000",
  warningColor: "#C65F00",
  attentionColor: "#FFC20D",
  blue: "#183799",
  lightBlue: "#2F96F4",
  black: "#000000",
  neutralColorGray: "#7F8598",
  LinkColor: "#003CFF",
  greenLight: "#2DB6A3",
  neutralGrayLight: "#AFB8D2",
  neutralGrayDark: "#3B425A",
  alto: "#D8D8D8",
  mercury: "#e1e1e1",
  gallery: "#ececec",
  silverChalice: "#A1A1A1",
  athensGray: "#ecedf3",
  tundora: "#464646",
  lightSilverChalice: "#aeaeae",
  silver: "#C2C2C2",
  alertRed: "#D92632",
  gray: "#848484",
  comet: "#5F677F",
  gullGray: "#979FB6",
  jungleGreen: "#239E8C",
  baliHai: "#808aaa",
  wildSand: "#F6F6F6",
  pippin: "#FFE2E4",
  stormGray: "#6E7489",
  borderColorGray: "#EFEFEF",
  butterflyBush: "#65558F",
  balticSea: "#1D1B20",
  dullLavender: "#8EA2E4",
  perano: "#9EB2EF",
  indigo: "#3D5CB9",
  periwinkle: "#D5DFFF",
  sweetPink: "#FFA0A0",
  waterloo: "#7C8398",
  lightMercury: "#E7E7E7",
  linkWater: "#E5EAF8",
  doveGray: "#666666",
  neutralSilverChalice: "#B2B2B2",
  alabster: "#F8F8F8",
  concrete: "#F2F2F2",
  atomicTangerice: "#FF9852",
  roofTerracotta: "#B3261E",
  stTropaz: "#2A4F8F",
  periwinkleGray: "#C8D1EB",
  darkRed: "#610000",
};

const defaultVariant = {
  name: THEMES.DEFAULT,
  palette: {
    mode: "light",
    primary: {
      main: customColor[700],
      contrastText: "#FFF",
    },
    secondary: {
      main: customColor[500],
      contrastText: "#FFF",
    },
    background: {
      default: "#F7F9FC",
      paper: "#FFF",
    },
  },
  header: {
    color: grey[900],
    background: "#FFF",
    search: {
      color: grey[800],
    },
    indicator: {
      background: customColor[600],
    },
  },
  footer: {
    color: grey[500],
    background: "#FFF",
  },
  sidebar: {
    color: grey[200],
    background: "#233044",
    header: {
      color: grey[200],
      background: "#233044",
      brand: {
        color: customColor[500],
      },
    },
    footer: {
      color: grey[200],
      background: "#1E2A38",
      online: {
        background: green[500],
      },
    },
    badge: {
      color: "#FFF",
      background: customColor[500],
    },
  },
};

const darkVariant = merge(defaultVariant, {
  name: THEMES.DARK,
  palette: {
    mode: "dark",
    primary: {
      main: customColor[600],
      contrastText: "#FFF",
    },
    background: {
      default: "#1B2635",
      paper: "#233044",
    },
    text: {
      primary: "rgba(255, 255, 255, 0.95)",
      secondary: "rgba(255, 255, 255, 0.5)",
    },
  },
  header: {
    color: grey[300],
    background: "#1B2635",
    search: {
      color: grey[200],
    },
  },
  footer: {
    color: grey[300],
    background: "#233044",
  },
});

const lightVariant = merge(defaultVariant, {
  name: THEMES.LIGHT,
  palette: {
    mode: "light",
  },
  header: {
    color: grey[200],
    background: customColor[800],
    search: {
      color: grey[100],
    },
    indicator: {
      background: red[700],
    },
  },
  sidebar: {
    color: grey[900],
    background: "#FFF",
    header: {
      color: "#FFF",
      background: customColor[800],
      brand: {
        color: "#FFFFFF",
      },
    },
    footer: {
      color: grey[800],
      background: "#F7F7F7",
      online: {
        background: green[500],
      },
    },
  },
});

const blueVariant = merge(defaultVariant, {
  name: THEMES.BLUE,
  palette: {
    mode: "light",
  },
  sidebar: {
    color: "#FFF",
    background: customColor[700],
    header: {
      color: "#FFF",
      background: customColor[800],
      brand: {
        color: "#FFFFFF",
      },
    },
    footer: {
      color: "#FFF",
      background: customColor[800],
      online: {
        background: "#FFF",
      },
    },
    badge: {
      color: "#000",
      background: "#FFF",
    },
  },
});

const greenVariant = merge(defaultVariant, {
  name: THEMES.GREEN,
  palette: {
    primary: {
      main: green[800],
      contrastText: "#FFF",
    },
    secondary: {
      main: green[500],
      contrastText: "#FFF",
    },
  },
  header: {
    indicator: {
      background: green[600],
    },
  },
  sidebar: {
    color: "#FFF",
    background: green[700],
    header: {
      color: "#FFF",
      background: green[800],
      brand: {
        color: "#FFFFFF",
      },
    },
    footer: {
      color: "#FFF",
      background: green[800],
      online: {
        background: "#FFF",
      },
    },
    badge: {
      color: "#000",
      background: "#FFF",
    },
  },
});

const indigoVariant = merge(defaultVariant, {
  name: THEMES.INDIGO,
  palette: {
    primary: {
      main: indigo[600],
      contrastText: "#FFF",
    },
    secondary: {
      main: indigo[400],
      contrastText: "#FFF",
    },
  },
  header: {
    indicator: {
      background: indigo[600],
    },
  },
  sidebar: {
    color: "#FFF",
    background: indigo[700],
    header: {
      color: "#FFF",
      background: indigo[800],
      brand: {
        color: "#FFFFFF",
      },
    },
    footer: {
      color: "#FFF",
      background: indigo[800],
      online: {
        background: "#FFF",
      },
    },
    badge: {
      color: "#000",
      background: "#FFF",
    },
  },
});

const variants: Array<VariantType> = [
  defaultVariant,
  darkVariant,
  lightVariant,
  blueVariant,
  greenVariant,
  indigoVariant,
];

export default variants;

export type VariantType = {
  name: string;
  palette: {
    primary: MainContrastTextType;
    secondary: MainContrastTextType;
  };
  header: ColorBgType & {
    search: {
      color: string;
    };
    indicator: {
      background: string;
    };
  };
  footer: ColorBgType;
  sidebar: ColorBgType & {
    header: ColorBgType & {
      brand: {
        color: string;
      };
    };
    footer: ColorBgType & {
      online: {
        background: string;
      };
    };
    badge: ColorBgType;
  };
};

type MainContrastTextType = {
  main: string;
  contrastText: string;
};
type ColorBgType = {
  color: string;
  background: string;
};
